import Phaser from 'phaser';
import { SoundManager } from '../systems/SoundManager';
import { MetaProgression } from '../systems/MetaProgression';

export class GameOverScene extends Phaser.Scene {
  private survivalTime!: number;
  private level!: number;
  private enemiesKilled!: number;
  private soundManager!: SoundManager;
  private metaProgression!: MetaProgression;
  private catTreatsEarned: number = 0;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: { survivalTime: number; level: number; enemiesKilled: number }): void {
    this.survivalTime = data.survivalTime || 0;
    this.level = data.level || 1;
    this.enemiesKilled = data.enemiesKilled || 0;
  }

  create(): void {
    this.soundManager = new SoundManager(this);
    this.metaProgression = new MetaProgression();

    // Record the run and get Cat Treats earned
    this.catTreatsEarned = this.metaProgression.recordRun(
      this.survivalTime,
      this.level,
      this.enemiesKilled
    );

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;

    // Dark gradient background
    const bg = this.add.rectangle(centerX, centerY, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.95);

    // Add subtle grid pattern
    const graphics = this.add.graphics();
    graphics.lineStyle(1, 0x333333, 0.2);
    for (let x = 0; x < this.cameras.main.width; x += 40) {
      graphics.lineBetween(x, 0, x, this.cameras.main.height);
    }
    for (let y = 0; y < this.cameras.main.height; y += 40) {
      graphics.lineBetween(0, y, this.cameras.main.width, y);
    }

    // Main stats panel background
    const panelWidth = 600;
    const panelHeight = 500;
    const panel = this.add.rectangle(centerX, centerY, panelWidth, panelHeight, 0x1a1a1a, 0.9);
    panel.setStrokeStyle(4, 0x4CAF50);

    // Game Over title with glow effect
    const titleGlow = this.add.text(centerX, centerY - 180, 'GAME OVER', {
      fontSize: '72px',
      color: '#FF4444',
      fontStyle: 'bold'
    });
    titleGlow.setOrigin(0.5);
    titleGlow.setAlpha(0.3);
    titleGlow.setScale(1.1);

    const title = this.add.text(centerX, centerY - 180, 'GAME OVER', {
      fontSize: '72px',
      color: '#FF0000',
      fontStyle: 'bold'
    });
    title.setOrigin(0.5);

    // Animate title
    this.tweens.add({
      targets: title,
      scale: { from: 0.8, to: 1 },
      alpha: { from: 0, to: 1 },
      duration: 500,
      ease: 'Back.out'
    });

    // Cat died message
    const subtitle = this.add.text(centerX, centerY - 120, '🐱 The mice overwhelmed you...', {
      fontSize: '24px',
      color: '#CCCCCC',
      fontStyle: 'italic'
    });
    subtitle.setOrigin(0.5);
    subtitle.setAlpha(0);

    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 500,
      delay: 300
    });

    // Stats section header
    const statsHeader = this.add.text(centerX, centerY - 70, '═══ FINAL STATS ═══', {
      fontSize: '22px',
      color: '#4CAF50',
      fontStyle: 'bold'
    });
    statsHeader.setOrigin(0.5);

    // Format survival time
    const minutes = Math.floor(this.survivalTime / 60);
    const seconds = this.survivalTime % 60;
    const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    // Calculate additional stats
    const enemiesPerMinute = this.survivalTime > 0 ? (this.enemiesKilled / (this.survivalTime / 60)).toFixed(1) : '0';
    const waveReached = Math.floor(this.survivalTime / 5); // Assuming 5 seconds per wave

    const stats = [
      { label: 'Survival Time', value: timeString, icon: '⏱️', color: '#FFD700' },
      { label: 'Level Reached', value: this.level.toString(), icon: '⭐', color: '#FF69B4' },
      { label: 'Mice Defeated', value: this.enemiesKilled.toString(), icon: '🐭', color: '#FF6B6B' },
      { label: 'Wave Reached', value: waveReached.toString(), icon: '🌊', color: '#4FC3F7' },
      { label: 'Kills/Minute', value: enemiesPerMinute, icon: '⚔️', color: '#9C27B0' }
    ];

    const statsY = centerY - 30;
    const lineHeight = 45;

    stats.forEach((stat, index) => {
      const y = statsY + (index * lineHeight);

      // Stats row background
      const rowBg = this.add.rectangle(centerX, y, panelWidth - 60, 38, 0x2a2a2a, 0.5);
      rowBg.setAlpha(0);

      const icon = this.add.text(centerX - 240, y, stat.icon, {
        fontSize: '28px'
      });
      icon.setOrigin(0, 0.5);
      icon.setAlpha(0);

      const label = this.add.text(centerX - 200, y, stat.label, {
        fontSize: '22px',
        color: '#FFFFFF'
      });
      label.setOrigin(0, 0.5);
      label.setAlpha(0);

      const value = this.add.text(centerX + 230, y, stat.value, {
        fontSize: '24px',
        color: stat.color,
        fontStyle: 'bold'
      });
      value.setOrigin(1, 0.5);
      value.setAlpha(0);

      // Animate each stat row
      this.tweens.add({
        targets: [rowBg, icon, label, value],
        alpha: 1,
        duration: 400,
        delay: 500 + (index * 100),
        ease: 'Power2'
      });

      // Bounce animation for values
      this.tweens.add({
        targets: value,
        scale: { from: 1.5, to: 1 },
        duration: 400,
        delay: 500 + (index * 100),
        ease: 'Back.out'
      });
    });

    // Performance rating
    const rating = this.getPerformanceRating();
    const ratingText = this.add.text(centerX, centerY + 160, rating.text, {
      fontSize: '20px',
      color: rating.color,
      fontStyle: 'bold'
    });
    ratingText.setOrigin(0.5);
    ratingText.setAlpha(0);

    this.tweens.add({
      targets: ratingText,
      alpha: 1,
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 1200,
      ease: 'Back.out'
    });

    // Cat Treats earned display
    const treatsText = this.add.text(centerX, centerY + 195, `🍪 +${this.catTreatsEarned} Cat Treats Earned!`, {
      fontSize: '24px',
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    });
    treatsText.setOrigin(0.5);
    treatsText.setAlpha(0);

    this.tweens.add({
      targets: treatsText,
      alpha: 1,
      scale: { from: 0.3, to: 1 },
      duration: 600,
      delay: 1400,
      ease: 'Back.out'
    });

    // Restart button
    const restartButton = this.add.rectangle(centerX - 80, centerY + 250, 230, 60, 0x4CAF50);
    restartButton.setInteractive({ useHandCursor: true });
    restartButton.setStrokeStyle(4, 0x66BB6A);
    restartButton.setAlpha(0);

    const restartText = this.add.text(centerX - 80, centerY + 250, '▶ RETRY', {
      fontSize: '24px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    restartText.setOrigin(0.5);
    restartText.setAlpha(0);

    // Upgrades button
    const upgradesButton = this.add.rectangle(centerX + 80, centerY + 250, 230, 60, 0x9C27B0);
    upgradesButton.setInteractive({ useHandCursor: true });
    upgradesButton.setStrokeStyle(4, 0xBA68C8);
    upgradesButton.setAlpha(0);

    const upgradesText = this.add.text(centerX + 80, centerY + 250, '⭐ UPGRADES', {
      fontSize: '24px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    upgradesText.setOrigin(0.5);
    upgradesText.setAlpha(0);

    // Animate buttons
    this.tweens.add({
      targets: [restartButton, restartText, upgradesButton, upgradesText],
      alpha: 1,
      duration: 500,
      delay: 1600
    });

    // Restart button interactions
    restartButton.on('pointerover', () => {
      restartButton.setFillStyle(0x66BB6A);
      this.tweens.add({
        targets: restartButton,
        scale: 1.05,
        duration: 200
      });
    });

    restartButton.on('pointerout', () => {
      restartButton.setFillStyle(0x4CAF50);
      this.tweens.add({
        targets: restartButton,
        scale: 1,
        duration: 200
      });
    });

    restartButton.on('pointerdown', () => {
      this.soundManager.playMenuClick();
      restartButton.setFillStyle(0x388E3C);
      this.cameras.main.fadeOut(300);
      this.time.delayedCall(300, () => {
        this.scene.start('GameScene');
      });
    });

    // Upgrades button interactions
    upgradesButton.on('pointerover', () => {
      upgradesButton.setFillStyle(0xBA68C8);
      this.tweens.add({
        targets: upgradesButton,
        scale: 1.05,
        duration: 200
      });
    });

    upgradesButton.on('pointerout', () => {
      upgradesButton.setFillStyle(0x9C27B0);
      this.tweens.add({
        targets: upgradesButton,
        scale: 1,
        duration: 200
      });
    });

    upgradesButton.on('pointerdown', () => {
      this.soundManager.playMenuClick();
      upgradesButton.setFillStyle(0x7B1FA2);
      this.cameras.main.fadeOut(300);
      this.time.delayedCall(300, () => {
        this.scene.start('MetaShopScene');
      });
    });

    // Keyboard input for restart
    this.input.keyboard?.once('keydown-SPACE', () => {
      this.soundManager.playMenuClick();
      this.cameras.main.fadeOut(300);
      this.time.delayedCall(300, () => {
        this.scene.start('GameScene');
      });
    });

    this.input.keyboard?.once('keydown-ENTER', () => {
      this.soundManager.playMenuClick();
      this.cameras.main.fadeOut(300);
      this.time.delayedCall(300, () => {
        this.scene.start('GameScene');
      });
    });

    // Instructions
    const instructions = this.add.text(centerX, centerY + 280, 'Press SPACE or ENTER to restart', {
      fontSize: '16px',
      color: '#888888',
      fontStyle: 'italic'
    });
    instructions.setOrigin(0.5);
    instructions.setAlpha(0);

    this.tweens.add({
      targets: instructions,
      alpha: { from: 0, to: 0.8 },
      duration: 1000,
      delay: 2000,
      yoyo: true,
      repeat: -1
    });

    // Fade in from black
    this.cameras.main.fadeIn(500);
  }

  private getPerformanceRating(): { text: string; color: string } {
    const survivalMinutes = this.survivalTime / 60;
    const killsPerMinute = survivalMinutes > 0 ? this.enemiesKilled / survivalMinutes : 0;

    if (survivalMinutes >= 10 && killsPerMinute >= 20) {
      return { text: '🏆 LEGENDARY SURVIVOR! 🏆', color: '#FFD700' };
    } else if (survivalMinutes >= 7 && killsPerMinute >= 15) {
      return { text: '⭐ EPIC PERFORMANCE! ⭐', color: '#FF6B6B' };
    } else if (survivalMinutes >= 5 && killsPerMinute >= 10) {
      return { text: '✨ GREAT JOB! ✨', color: '#4FC3F7' };
    } else if (survivalMinutes >= 3) {
      return { text: '👍 NICE TRY! 👍', color: '#9CCC65' };
    } else {
      return { text: '🐱 KEEP PRACTICING! 🐱', color: '#FFB74D' };
    }
  }
}
